import { Service, inject } from '@angular/core';
import {environment} from '../../environments/environment';
import {combineLatest, Observable, Subject} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {UnitRatingRequest} from '../model/unit-rating-request';
import {UnitRatingResponse} from '../model/unit-rating-response';
import {SelectionUtils} from '../configurator/selection-utils';
import {ModelService} from './model-service';
import {GuruEntity} from '../model/guru-entity';
import {CoilRequest} from '../model/coil-request';
import {GuruCharacteristic} from '../model/guru-characteristic';
import {StringUtils} from "../utils/string-utils";

@Service()
export class RatingService {
  private readonly http = inject(HttpClient);
  private readonly modelService = inject(ModelService);


  private readonly URL_BASE = environment.ratingsBaseUrl;
  private readonly API_KEY = '1E9FCDA9-0AF0-484E-B710-0CBAFAA6E605';

  getRatingResponse(engineKey: string, selectionEntities: GuruEntity[]): Observable<UnitRatingResponse> {
    const subject = new Subject<UnitRatingResponse>();
    this.buildRatingRequest(engineKey, selectionEntities).subscribe(request => {
      this.getUnitRatingResponse(request).subscribe({
        next: (response) => {
          subject.next(response);
        }
      });
    }, error => subject.error(error));
    return subject.asObservable();
  }

  getUnitRatingResponse(request: UnitRatingRequest): Observable<UnitRatingResponse> {
    const subject = new Subject<UnitRatingResponse>();
    this.http.post<UnitRatingResponse>(this.URL_BASE, JSON.stringify(request),
      {
        headers: this.getPostHeaders('application/json', 'application/json')
      }).subscribe({
      next: (response) => {
        response.primaryCooling = false;
        if (response.HighSpeed) {
          response.PrimaryCoilResponse = response.HighSpeed.PrimaryCoilResponse;
          response.SecondaryCoilResponse = response.HighSpeed.SecondaryCoilResponse;
          response.Airflow = response.HighSpeed.Airflow;
          response.CircuitResponse = response.HighSpeed.CircuitResponse;
          response.ElectricHeatResponse = response.HighSpeed.ElectricHeatResponse;
        }
        if (response.PrimaryCoilResponse) {
          const coilResponse = response.PrimaryCoilResponse;
          const coilRequest = request.PrimaryCoilRequest;
          response.primaryCooling = !isNaN(coilRequest.SaturationSuctionTemperature)
            || coilRequest.EnteringFluidTemperature < coilRequest.EnteringDryBulb;
          coilResponse.FluidTemperatureChange =
            Math.round(Math.abs(coilResponse.LeavingFluidTemperature - coilRequest.EnteringFluidTemperature) * 10) / 10;
        }
        if (response.SecondaryCoilResponse) {
          const coilResponse = response.SecondaryCoilResponse;
          const coilRequest = request.SecondaryCoilRequest;
          coilResponse.FluidTemperatureChange =
            Math.round(Math.abs(coilResponse.LeavingFluidTemperature - coilRequest.EnteringFluidTemperature) * 10) / 10;
        }
        subject.next(response);
      },
      error: (error) => subject.error(error),
      complete: () => subject.complete()
    });
    return subject.asObservable();
  }

  private getPostHeaders(contentType: string, acceptType: string) {
    return this.getRequestHeaders(acceptType).set('Content-Type', contentType);
  }

  private getRequestHeaders(acceptType: string) {
    return new HttpHeaders().set('Accept', acceptType).set('ApiKey', this.API_KEY);
  }

  private buildRatingRequest(engineKey: string, selectionEntities: GuruEntity[]): Observable<UnitRatingRequest> {
    const subject = new Subject<UnitRatingRequest>();
    this.getRatingInputCharacteristics(engineKey).subscribe(characteristics => {
      const request = new UnitRatingRequest();
      request.IsMetricInput = this.getStringSelectionValue(selectionEntities, 'USMetricInput') === 'true';
      request.Altitude = this.getNumericSelectionValue(selectionEntities, 'USAltitude');
      request.Airflow = this.getNumericSelectionValue(selectionEntities, 'USFAirflow');
      request.FanSpeed = this.getStringSelectionValue(selectionEntities, 'USFSpeed');
      request.Model = this.getStringSelectionValue(selectionEntities, 'USUnit');
      request.Size = this.getStringSelectionValue(selectionEntities, 'USSize');
      request.MotorType = this.getInputCharacteristicValue(characteristics, 'USMotorType', 'Code');
      request.MotorConfiguration = this.getInputCharacteristicValue(characteristics, 'USUnitVoltage', 'Description');
      request.PrimaryCoilRequest = this.buildPrimaryCoilRequest(selectionEntities, characteristics);
      request.ElectricHeatRequest = this.buildElectricHeatRequest(selectionEntities, characteristics);
      request.Filter = this.getStringSelectionValue(selectionEntities, 'USFilter');
      request.SecondaryCoilRequest = this.buildSecondaryCoilRequest(selectionEntities, characteristics);
      const tspSelected = this.getStringSelectionValue(selectionEntities, 'USFStatic');
      if (tspSelected && 'true' === tspSelected.toLowerCase().trim()) {
        request.TotalStaticPressure = this.getNumericSelectionValue(selectionEntities, 'USFESP');
      } else {
        request.ExternalStaticPressure = this.getNumericSelectionValue(selectionEntities, 'USFESP');
      }
      subject.next(request);
    }, error => subject.error(error));
    return subject.asObservable();
  }

  private getInputCharacteristicValue(characteristics: GuruCharacteristic[], entityName: string, characteristicName: string): string {
    if (!characteristics || characteristics.length === 0) {
      return null!;
    }
    const filtered = characteristics.filter(entry => entry.propertyName === characteristicName && entry.entityName === entityName);
    if (filtered && filtered.length === 1) {
      return filtered[0].value;
    }
    return null!;
  }

  private getRatingInputCharacteristics(engineKey: string): Observable<GuruCharacteristic[]> {
    const observables: Observable<GuruCharacteristic>[] = [];
    observables.push(this.modelService.getGuruCharacteristic(engineKey, 'USMotorType', 'Code'));
    observables.push(this.modelService.getGuruCharacteristic(engineKey, 'USUnitVoltage', 'Description'));
    observables.push(this.modelService.getGuruCharacteristic(engineKey, 'USCoilRows', 'PriRows'));
    observables.push(this.modelService.getGuruCharacteristic(engineKey, 'USCoilRows', 'SecRows'));
    observables.push(this.modelService.getGuruCharacteristic(engineKey, 'USSystemType', 'ElecHt'));
    observables.push(this.modelService.getGuruCharacteristic(engineKey, 'USSystemType', 'DxCoil'));
    observables.push(this.modelService.getGuruCharacteristic(engineKey, 'USSystemType', 'PriCoilApp'));
    observables.push(this.modelService.getGuruCharacteristic(engineKey, 'USSystemType', 'SecCoilApp'));
    const subject = new Subject<GuruCharacteristic[]>();
    combineLatest(observables).subscribe(results => {
      subject.next(results);
    }, error => subject.error(error));
    return subject.asObservable();
  }

  private getNumericSelectionValue(selectionEntities: GuruEntity[], entityName: string): number {
    const stringValue = this.getStringSelectionValue(selectionEntities, entityName);
    return stringValue == null ? null! : +stringValue;
  }

  private getStringSelectionValue(selectionEntities: GuruEntity[], entityName: string): string {
    const entity = SelectionUtils.getEntity(selectionEntities, entityName);
    return (entity == null || entity.Value == null) ? null! : entity.Value.toString();
  }

  private buildPrimaryCoilRequest(selectionEntities: GuruEntity[], characteristics: GuruCharacteristic[]): CoilRequest {
    const application = this.getInputCharacteristicValue(characteristics, 'USSystemType', 'PriCoilApp');
    if (!application || ('C' !== application.toUpperCase().trim() && 'H' !== application.toUpperCase().trim())) {
      return null!;
    }
    const request = ('C' === application.toUpperCase().trim())
      ? this.getCoolingCoilRequest(selectionEntities, characteristics)
      : this.getHeatingCoilRequest(selectionEntities);
    request.RowCount = this.getNumericCharacteristicValue(characteristics, 'USCoilRows', 'PriRows');
    request.NumberOfCircuits = this.getNumericSelectionValue(selectionEntities, 'USPrimaryCoilCircuit');
    return request;
  }

  private getCoolingCoilRequest(selectionEntities: GuruEntity[], characteristics: GuruCharacteristic[]): CoilRequest {
    const request = new CoilRequest();
    request.EnteringDryBulb = this.getNumericSelectionValue(selectionEntities, 'USCEDB');
    request.EnteringWetBulb = this.getNumericSelectionValue(selectionEntities, 'USCEWB');
    const isRefrigerant = this.getBooleanCharacteristicValue(characteristics, 'USSystemType', 'DxCoil');
    if (isRefrigerant) {
      request.SaturationSuctionTemperature = this.getNumericSelectionValue(selectionEntities, 'USCEWT');
      request.FluidType = 'R410A';
    } else {
      request.EnteringFluidTemperature = this.getNumericSelectionValue(selectionEntities, 'USCEWT');
      request.FluidType = this.getStringSelectionValue(selectionEntities, 'USCFluid');
      request.GlycolPercentage = this.getNumericSelectionValue(selectionEntities, 'USCFluidMix');
    }
    const temperatureChange = this.getNumericSelectionValue(selectionEntities, 'USCWTC');
    if (temperatureChange) {
      request.TemperatureChange = temperatureChange;
    } else {
      request.FlowRate = this.getNumericSelectionValue(selectionEntities, 'USCWFR');
    }
    return request;
  }

  private getHeatingCoilRequest(selectionEntities: GuruEntity[]): CoilRequest {
    const request = new CoilRequest();
    request.EnteringDryBulb = this.getNumericSelectionValue(selectionEntities, 'USHEDB');
    request.EnteringWetBulb = 65;
    request.EnteringFluidTemperature = this.getNumericSelectionValue(selectionEntities, 'USHEWT');
    request.FluidType = this.getStringSelectionValue(selectionEntities, 'USHFluid');
    request.GlycolPercentage = this.getNumericSelectionValue(selectionEntities, 'USHFluidMix');
    const temperatureChange = this.getNumericSelectionValue(selectionEntities, 'USHWTC');
    if (temperatureChange) {
      request.TemperatureChange = temperatureChange;
    } else {
      request.FlowRate = this.getNumericSelectionValue(selectionEntities, 'USHWFR');
    }
    return request;
  }

  private getBooleanCharacteristicValue(characteristics: GuruCharacteristic[], entityName: string, characteristicName: string): boolean {
    const value = this.getInputCharacteristicValue(characteristics, entityName, characteristicName);
    return (value && value.toLowerCase().trim()) === 'true';
  }

  private getNumericCharacteristicValue(characteristics: GuruCharacteristic[], entityName: string, characteristicName: string): number {
    const value = this.getInputCharacteristicValue(characteristics, entityName, characteristicName);
    return (value && !isNaN(+value)) ? +value : null!;
  }

  private buildElectricHeatRequest(selectionEntities: GuruEntity[], characteristics: GuruCharacteristic[]): CoilRequest {
    const isElectricHeat = this.getBooleanCharacteristicValue(characteristics, 'USSystemType', 'ElecHt');
    if (!isElectricHeat) {
      return null!;
    }
    const request = new CoilRequest();
    request.ElectricHeatKilowatts = (this.getNumericSelectionValue(selectionEntities, 'USElecHtKw') || 0) / 1000;
    request.EnteringWetBulb = 65;
    request.EnteringDryBulb = this.getNumericSelectionValue(selectionEntities, 'USHEDB');
    return request;
  }

  private buildSecondaryCoilRequest(selectionEntities: GuruEntity[], characteristics: GuruCharacteristic[]): CoilRequest {
    const application = this.getInputCharacteristicValue(characteristics, 'USSystemType', 'SecCoilApp');
    if (!StringUtils.equalsIgnoreCase('C', application?.trim()) && !StringUtils.equalsIgnoreCase('H', application?.trim())) {
      return null!;
    }
    const request = StringUtils.equalsIgnoreCase('C', application.trim())
      ? this.getCoolingCoilRequest(selectionEntities, characteristics)
      : this.getHeatingCoilRequest(selectionEntities);
    request.RowCount = this.getNumericCharacteristicValue(characteristics, 'USCoilRows', 'SecRows');
    if (request.RowCount === 0) {
      request.NumberOfCircuits = this.getNumericSelectionValue(selectionEntities, 'USPrimaryCoilCircuit');
    } else {
      request.NumberOfCircuits = this.getNumericSelectionValue(selectionEntities, 'USSecondaryCoilCircuit');
    }
    return request;
  }

}
